import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Button,
  IconButton,
  Typography,
} from "@mui/material";
import { RemoveCircleOutline } from "@mui/icons-material";
import { fonts, colors, size } from "../ColorFont/color";
import { url } from "../ConstUrl/Url";
import { useTranslation } from "react-i18next";

const EditPlot = ({ plot, onClose }) => {
    const { t } = useTranslation();
  
  const [plotDetails, setPlotDetails] = useState(plot);
  const [photos, setPhotos] = useState(plot.photos || []);
  const [selectedCategory, setSelectedCategory] = useState(""); // Selected category
  const [categories, setCategories] = useState([]); // To store fetched categories
  const [subCategories, setSubCategories] = useState([]);

  // Fetch categories on component mount
  useEffect(() => {
    fetch(`${url}/api/get-categories`)
      .then((response) => response.json())
      .then((data) => {
        setCategories(data);
      })
      .catch((error) => console.error(error));
  }, []);

  // Update subcategories when the selected category changes
  useEffect(() => {
    if (selectedCategory) {
      const category = categories.find(
        (category) => category.categoryType === selectedCategory
      );
      if (category) {
        setSubCategories(category.subCategories);
      }
    }
  }, [selectedCategory, categories]);

  // Update state when the plot prop changes
  useEffect(() => {
    setPlotDetails(plot);
    setPhotos(plot.photos || []);
    setSelectedCategory(plot.category || "");
  }, [plot]);

  // Handle input changes
  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === "category") {
      const category = categories.find(
        (category) => category.categoryType === value
      );
      if (category) {
        setSubCategories(category.subCategories);
      } else {
        setSubCategories([]);
      }
      setSelectedCategory(value);
      setPlotDetails({ ...plotDetails, category: value });
    } else if (name === "subCategory") {
      setPlotDetails({ ...plotDetails, subCategory: value });
    } else {
      setPlotDetails({ ...plotDetails, [name]: value });
    }
  };

  // Remove a photo from the list
  const handleRemovePhoto = (index) => {
    const newPhotos = [...photos];
    newPhotos.splice(index, 1);
    setPhotos(newPhotos);
  };

  // Handle new photo uploads
  const handlePhotoChange = (event) => {
    const newFiles = Array.from(event.target.files);
    if (newFiles.length > 0) {
      setPhotos((prevPhotos) => [...prevPhotos, ...newFiles]);
    }
  };

  // Submit updated plot details
  const handleSubmit = async (event) => {
    event.preventDefault();
    const formData = new FormData();

    // Append plot details to formData
    Object.entries(plotDetails).forEach(([key, value]) => {
      formData.append(key, value);
    });

    // Append photos to formData
    for (const photo of photos) {
      if (typeof photo === "string") {
        // Existing photo URL string
        formData.append("photos", photo);
      } else {
        // New file object
        formData.append("photos", photo, photo.name);
      }
    }

    try {
      const response = await fetch(`${url}/api/plots/update/${plot._id}`, {
        method: "PUT",
        body: formData,
      });
      if (response.ok) {
        alert("Plot details updated successfully!");
      } else {
        const errorData = await response.json();
        alert(`Failed to update: ${errorData.error}`);
      }
    } catch (error) {
      alert("An error occurred while updating the plot details.");
    }
    onClose();
  };

  return (
    <Box
      sx={{
        padding: "14px",
        borderRadius: 2,
        maxWidth: 800,
        margin: "auto",
      }}
    >
      <Typography
        sx={{
          fontFamily: fonts.poppins,
          marginBottom: 3,
          fontSize: size.title,
          fontWeight: 600,
          color: colors.primary_dark,
          textAlign: "center",
        }}
      >
        {t("UpdatePlotDetails")}
      </Typography>
      <form onSubmit={handleSubmit}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: 2,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: fonts.poppins,
                marginBottom: 1,
                fontWeight: 600,
                color: colors.text,
              }}
            >
              {t("area")}
            </Typography>
            <TextField
              name="area"
              value={plotDetails.area}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Box>
          <Box>
            <Typography
              sx={{
                fontFamily: fonts.poppins,
                fontWeight: 600,
                marginBottom: 1,
                color: colors.text,
              }}
            >
              {t("selectCrop")}
            </Typography>
            <FormControl size="small" fullWidth>
              <Select
                value={plotDetails.category}
                onChange={handleChange}
                name="category"
              >
                {categories.map((category) => (
                  <MenuItem key={category._id} value={category.categoryType}>
                    {category.categoryType}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography
              sx={{
                fontFamily: fonts.poppins,
                fontWeight: 600,
                marginBottom: 1,
                color: colors.text,
              }}
            >
              {t("selectSubCrop")}
            </Typography>
            <FormControl size="small" fullWidth>
              <Select
                value={plotDetails.subCategory}
                onChange={handleChange}
                name="subCategory"
              >
                {subCategories.map((subcategory) => (
                  <MenuItem key={subcategory} value={subcategory}>
                    {subcategory}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography
              sx={{
                fontFamily: fonts.poppins,
                fontWeight: 600,
                marginBottom: 1,
                color: colors.text,
              }}
            >
              {t("amount")}
            </Typography>
            <TextField
              name="amount"
              value={plotDetails.amount}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Box>
          <Box>
            <Typography
              sx={{
                fontFamily: fonts.poppins,
                fontWeight: 600,
                marginBottom: 1,
                color: colors.text,
              }}
            >
              {t("contactNo")}
            </Typography>
            <TextField
              name="contactNumber"
              value={plotDetails.contactNumber}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Box>
        </Box>

        <Box sx={{ marginTop: 3 }}>
          <Typography
            sx={{
              fontFamily: fonts.poppins,
              marginBottom: 1,
              fontWeight: 600,
              color: colors.text,
            }}
          >
            {t("photos")}
          </Typography>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handlePhotoChange}
          />
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, marginTop: 2 }}>
            {photos.map((photo, index) => (
              <Box
                key={index}
                sx={{
                  position: "relative",
                  width: 100,
                  height: 100,
                  border: "1px solid #ddd",
                  borderRadius: 1,
                }}
              >
                {typeof photo === "string" ? (
                  <img
                    src={photo}
                    alt="Plot"
                    width="100%"
                    height="100%"
                    style={{ borderRadius: 4 }}
                  />
                ) : (
                  <img
                    src={URL.createObjectURL(photo)}
                    alt="Plot"
                    width="100%"
                    height="100%"
                    style={{ borderRadius: 4 }}
                  />
                )}
                <IconButton
                  onClick={() => handleRemovePhoto(index)}
                  sx={{
                    position: "absolute",
                    top: -5,
                    right: -5,
                    backgroundColor: "rgba(0,0,0,0.5)",
                    color: "#fff",
                  }}
                >
                  <RemoveCircleOutline fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            marginTop: 4,
            alignItems: "center",
          }}
        >
          <Button
            variant="contained"
            type="submit"
            sx={{
              fontFamily: fonts.poppins,
              width: "30%",
              backgroundImage: colors.button,
            }}
          >
            {t("update")}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default EditPlot;
